import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import CepForm from '../../forms/CepForm/CepForm';
import FormFieldFile from '../../forms/FormFieldFile';
import MaskedInput from '../../forms/MaskedInput/MaskedInput';
import { condicaoAtendida, ehCampoConsentimento, ehVerdadeiro, rotuloCampo, valorForcado } from './regrasCadastro';

// Preenchidos pela busca do CEP — o usuário não edita à mão.
const CAMPOS_DO_CEP = ['endereco_logradouro', 'endereco_cidade', 'endereco_uf'];

const valorDaOpcao = (opcao) => opcao.valor ?? opcao.value;

/**
 * Um campo do formulário dinâmico de cadastro, conforme o `tipo` configurado.
 *
 * - `alterar(mudancas)`: grava no Formik sem salvar rascunho (digitação em texto);
 * - `alterarESalvar(mudancas)`: grava e agenda o rascunho (select, data, liga/desliga, CEP);
 * - `aoSair()`: blur — salva o rascunho do que foi digitado.
 * - `aoAlternar(codigo, marcado)`: opcional, intercepta o liga/desliga (ex.: abrir o termo
 *   antes de aceitar);
 * - `erro`: mensagem de erro própria do campo (ex.: CPF já cadastrado).
 * - `logradouroSemNome`: logradouro gravado quando o CEP não tem rua (zona rural).
 *
 * Devolve null quando o campo está oculto por `visivelSe`.
 */
export default function CampoCadastro({
  campo,
  valores,
  valoresSalvos,
  alterar,
  alterarESalvar,
  aoSair,
  aoAlternar,
  erro,
  emailFixo = false,
  logradouroSemNome,
}) {
  const { t, i18n } = useTranslation();
  const { codigo, tipo, obrigatorio, placeholder, opcoes, configuracao } = campo;

  if (configuracao?.visivelSe && !condicaoAtendida(configuracao.visivelSe, valores)) return null;

  const rotulo = rotuloCampo(campo, t, i18n.language);
  const idRotulo = `${codigo}-rotulo`; // liga o rótulo ao select (acessibilidade)
  const valor = valores[codigo];
  const travadoPorRegra = valorForcado(campo, valores) !== null;
  const bloqueado = travadoPorRegra || CAMPOS_DO_CEP.includes(codigo);

  // `semMascaraSe` desliga a máscara quando a condição bate (ex.: celular de estrangeiro).
  const mascara =
    configuracao?.mascara && !condicaoAtendida(configuracao.semMascaraSe, valores) ? configuracao.mascara : null;

  const textoBase = {
    fullWidth: true,
    name: codigo,
    label: rotulo,
    required: obrigatorio,
    placeholder,
    disabled: bloqueado,
    value: valor || '',
    onChange: (e) => alterar({ [codigo]: e.target.value }),
    onBlur: aoSair,
    error: Boolean(erro),
    helperText: erro || undefined,
    InputProps: mascara ? { inputComponent: MaskedInput, inputProps: { mask: mascara } } : undefined,
  };

  // UF vem do CEP: sempre texto, mesmo que o formulário a configure como select.
  if (codigo === 'endereco_uf') return <TextField {...textoBase} InputProps={undefined} />;

  if (codigo === 'cep') {
    return (
      <CepForm
        cepValue={valor || ''}
        onCepChange={(cep) => alterarESalvar({ cep })}
        onAddressLoad={({ endereco, cidade, estado }) =>
          alterarESalvar({
            endereco_logradouro: endereco || logradouroSemNome,
            ...(cidade ? { endereco_cidade: cidade } : {}),
            ...(estado ? { endereco_uf: estado } : {}),
          })
        }
        handleBlur={aoSair}
        required={Boolean(obrigatorio)}
        error={Boolean(erro)}
        helperText={erro}
      />
    );
  }

  const opcoesDoSelect = (opcoes ?? []).map((opcao) => (
    <MenuItem key={valorDaOpcao(opcao)} value={valorDaOpcao(opcao)}>
      {t(`opcao.${valorDaOpcao(opcao)}`, { defaultValue: opcao.label })}
    </MenuItem>
  ));

  switch (tipo) {
    case 'text':
      return <TextField {...textoBase} />;

    case 'textarea':
      return <TextField {...textoBase} InputProps={undefined} multiline minRows={4} />;

    case 'email':
      return <TextField {...textoBase} InputProps={undefined} type="email" disabled={bloqueado || emailFixo} />;

    case 'date':
      return (
        <DatePicker
          label={rotulo}
          value={valor || null}
          onChange={(data) => alterarESalvar({ [codigo]: data })}
          disabled={bloqueado}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              name={codigo}
              required={obrigatorio}
              onBlur={aoSair}
              error={Boolean(erro) || params.error}
              helperText={erro || params.helperText}
            />
          )}
        />
      );

    case 'select':
      return (
        <FormControl fullWidth required={obrigatorio} disabled={bloqueado} error={Boolean(erro)}>
          <InputLabel id={idRotulo}>{rotulo}</InputLabel>
          <Select
            labelId={idRotulo}
            name={codigo}
            label={rotulo}
            value={valor || ''}
            onChange={(e) => alterarESalvar({ [codigo]: e.target.value })}
            onBlur={aoSair}
          >
            {opcoesDoSelect}
          </Select>
          {erro && <FormHelperText>{erro}</FormHelperText>}
        </FormControl>
      );

    case 'multiselect': {
      const selecionados = Array.isArray(valor) ? valor : [];
      const rotuloDaOpcao = (v) => {
        const opcao = opcoes?.find((o) => valorDaOpcao(o) === v);
        return opcao ? t(`opcao.${v}`, { defaultValue: opcao.label }) : v;
      };
      return (
        <FormControl fullWidth required={obrigatorio} disabled={bloqueado} error={Boolean(erro)}>
          <InputLabel id={idRotulo}>{rotulo}</InputLabel>
          <Select
            labelId={idRotulo}
            multiple
            name={codigo}
            label={rotulo}
            value={selecionados}
            onChange={(e) => alterarESalvar({ [codigo]: e.target.value })}
            onBlur={aoSair}
            renderValue={(lista) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {lista.map((v) => (
                  <Chip key={v} label={rotuloDaOpcao(v)} size="small" />
                ))}
              </Box>
            )}
          >
            {opcoesDoSelect}
          </Select>
          {erro && <FormHelperText>{erro}</FormHelperText>}
        </FormControl>
      );
    }

    case 'checkbox':
    case 'switch': {
      // Consentimento já aceito no que está salvo fica travado (ver ehCampoConsentimento).
      const consentimentoAceito = ehCampoConsentimento(codigo) && ehVerdadeiro(valoresSalvos?.[codigo]);
      const desabilitado = travadoPorRegra || consentimentoAceito;
      const alternar = (marcado) => (aoAlternar ? aoAlternar(codigo, marcado) : alterarESalvar({ [codigo]: marcado }));
      return (
        <Box>
          <FormControlLabel
            disabled={desabilitado}
            label={rotulo}
            sx={erro ? { color: 'error.main' } : undefined}
            control={
              <Switch
                name={codigo}
                checked={ehVerdadeiro(valor)}
                disabled={desabilitado}
                onChange={(e) => alternar(e.target.checked)}
              />
            }
          />
          {erro && <FormHelperText error>{erro}</FormHelperText>}
        </Box>
      );
    }

    case 'file':
      return (
        <Box>
          <FormFieldFile
            campo={campo}
            valor={valor || ''}
            onChange={(c, v) => alterarESalvar({ [c]: v })}
            disabled={travadoPorRegra}
          />
          {erro && <FormHelperText error>{erro}</FormHelperText>}
        </Box>
      );

    default:
      return null;
  }
}
